<template>
  <div class="product-page">
    <div class="toolbar">
      <a-input v-model="query.name" placeholder="产品名称" class="query-control" />
      <a-textarea v-model="query.models" placeholder="型号（多行或逗号分隔）" :auto-size="{ minRows: 1, maxRows: 4 }" class="query-control" />
      <a-textarea v-model="query.brands" placeholder="品牌（多行或逗号分隔）" :auto-size="{ minRows: 1, maxRows: 4 }" class="query-control" />
      <a-select v-model="query.categoryId" placeholder="产品大项" allowClear class="query-control" @change="handleQueryCategoryChange">
        <a-select-option v-for="item in categoryTree" :key="item.id" :value="item.id">{{ item.name }}</a-select-option>
      </a-select>
      <a-select v-model="query.subCategoryId" placeholder="产品种类" allowClear class="query-control">
        <a-select-option v-for="item in querySubCategories" :key="item.id" :value="item.id">{{ item.name }}</a-select-option>
      </a-select>
      <a-button type="primary" @click="handleSearch">查询</a-button>
      <a-button @click="resetSearch">重置</a-button>
      <a-button type="primary" icon="plus" @click="handleCreate">新增产品</a-button>
      <a-button icon="upload" @click="openImport">导入产品</a-button>
    </div>

    <a-table
      :columns="columns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      :scroll="{ x: 1280 }"
      @change="handleTableChange"
    >
      <span slot="status" slot-scope="text">
        <a-tag :color="text === 1 ? 'green' : 'red'">{{ text === 1 ? '上架' : '下架' }}</a-tag>
      </span>
      <span slot="price" slot-scope="text, record" class="price-links">
        <template v-if="record.priceTiers && record.priceTiers.length">
          <a v-for="tier in record.priceTiers" :key="tier.id" @click="openFieldEdit(record, 'price', tier)">{{ tier.minQuantity }}+ ￥{{ Number(tier.price).toFixed(3) }}</a>
        </template>
        <span v-else class="no-price">暂无价格/联系询价</span>
      </span>
      <a slot="stock" slot-scope="text, record" @click="openFieldEdit(record, 'stock')">{{ text }}</a>
      <span slot="action" slot-scope="text, record" class="action-links">
        <a @click="handleEdit(record)">编辑</a>
        <a-divider type="vertical" />
        <a-popconfirm
          :title="record.status === 1 ? '确定下架该产品吗？' : '确定上架该产品吗？'"
          @confirm="handleToggleStatus(record)"
        >
          <a>{{ record.status === 1 ? '下架' : '上架' }}</a>
        </a-popconfirm>
        <a-divider type="vertical" />
        <a-popconfirm title="确定删除该产品及其参数、文件吗？" @confirm="handleDelete(record.id)">
          <a class="danger-link">删除</a>
        </a-popconfirm>
      </span>
    </a-table>

    <a-modal
      v-model="importVisible"
      title="导入产品"
      :width="1200"
      :confirm-loading="importing"
      :mask-closable="false"
      :closable="!importing"
      :cancel-button-props="{ props: { disabled: importing } }"
      :body-style="{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }"
      ok-text="开始导入"
      cancel-text="取消"
      @ok="submitImport"
      @cancel="closeImport"
    >
      <div class="import-actions">
        <span>产品图片填 xxx.png、xxx.jpg，产品文件填 xxx.pdf。</span>
        <a-button type="link" icon="download" :loading="templateDownloading" @click="downloadImportTemplate">下载导入模板</a-button>
      </div>
      <a-collapse class="import-guide" :bordered="false" :default-active-key="['guide']">
        <a-collapse-panel key="guide" header="填表说明">
          <div class="import-guide-content">
            <span>1. 请先下载最新模板，不要删除或修改表头。</span>
            <span>2. 重量单位为 g；价格阶梯可留空，留空后前台显示“暂无价格/联系询价”。</span>
            <span>3. 价格阶梯填写：1-100：6，101-1000：5.8。</span>
            <span>4. 产品参数填写：参数a：sss，电阻：6欧。</span>
            <span>5. 产品图片填写原文件名，例如 xxx.png、xxx.jpg；没有可留空。</span>
            <span>6. 产品文件只支持 PDF，填写原文件名，例如 xxx.pdf；没有可留空。</span>
            <span>7. 中英文冒号、逗号、分号均可识别；缺少文件时确认后仍可继续导入。</span>
          </div>
        </a-collapse-panel>
      </a-collapse>
      <div class="import-upload-grid">
        <section class="import-upload-panel">
          <h3>上传产品表格</h3>
          <a-upload-dragger
            accept=".xlsx"
            :multiple="false"
            :file-list="importFileList"
            :before-upload="beforeImportUpload"
            :remove="removeImportFile"
          >
            <p class="ant-upload-drag-icon"><a-icon type="inbox" /></p>
            <p class="ant-upload-text">点击或拖拽上传 Excel</p>
            <p class="ant-upload-hint">支持 .xlsx，最多 1000 条</p>
          </a-upload-dragger>
        </section>
        <section class="import-upload-panel">
          <h3>上传产品图片</h3>
          <a-upload
            list-type="picture-card"
            accept="image/*,.png,.jpg,.jpeg,.gif,.webp,.bmp"
            multiple
            :file-list="importImageFileList"
            :before-upload="beforeImportImageUpload"
            :remove="removeImportImage"
            @preview="previewImportImage"
          >
            <div><a-icon type="plus" /><div class="upload-text">选择图片</div></div>
          </a-upload>
          <p class="image-upload-hint">图片文件名必须与表格“产品图片”列一致，单张不超过 5MB。</p>
        </section>
        <section class="import-upload-panel">
          <h3>上传 PDF 文件</h3>
          <a-upload-dragger
            accept=".pdf,application/pdf"
            multiple
            :file-list="importPdfFileList"
            :before-upload="beforeImportPdfUpload"
            :remove="removeImportPdf"
          >
            <p class="ant-upload-drag-icon"><a-icon type="file-pdf" /></p>
            <p class="ant-upload-text">点击或拖拽上传 PDF</p>
            <p class="ant-upload-hint">文件名必须与表格“产品文件”列一致，单个不超过 20MB</p>
          </a-upload-dragger>
        </section>
      </div>
      <a-alert v-if="imageCheck" class="image-check-result" :type="imageCheck.ok ? 'success' : 'error'" show-icon>
        <template slot="message">{{ imageCheck.ok ? `图片匹配完成，共 ${imageCheck.matchedCount} 张` : '图片与表格未完全匹配' }}</template>
        <div slot="description">
          <div v-if="imageCheck.missing.length">缺少图片：{{ imageCheck.missing.join('、') }}</div>
          <div v-if="imageCheck.extra.length">多余图片：{{ imageCheck.extra.join('、') }}</div>
          <div v-if="imageCheck.ok">表格引用的图片均已上传，且没有多余图片。</div>
        </div>
      </a-alert>
      <a-alert v-if="pdfCheck" class="image-check-result" :type="pdfCheck.ok ? 'success' : 'error'" show-icon>
        <template slot="message">{{ pdfCheck.ok ? `PDF匹配完成，共 ${pdfCheck.matchedCount} 个` : 'PDF与表格未完全匹配' }}</template>
        <div slot="description">
          <div v-if="pdfCheck.missing.length">缺少 PDF：{{ pdfCheck.missing.join('、') }}</div>
          <div v-if="pdfCheck.extra.length">多余 PDF：{{ pdfCheck.extra.join('、') }}</div>
          <div v-if="pdfCheck.ok">表格引用的 PDF 均已上传，且没有多余文件。</div>
        </div>
      </a-alert>
      <a-alert
        class="import-tip"
        type="info"
        show-icon
        message="若文件中存在系统未收录的产品大项或产品种类，导入前会列出并询问是否直接创建。"
      />
    </a-modal>

    <a-modal v-model="fieldVisible" :title="fieldForm.field === 'stock' ? '修改库存' : '修改单价'" :confirm-loading="fieldSaving"
      :mask-closable="false" :closable="!fieldSaving" :cancel-button-props="{ props: { disabled: fieldSaving } }"
      ok-text="保存" cancel-text="取消" @ok="saveField">
      <a-form-model layout="vertical">
        <a-form-model-item label="产品型号"><span>{{ fieldForm.model }}</span></a-form-model-item>
        <a-form-model-item v-if="fieldForm.field === 'price' && fieldForm.minQuantity" label="阶梯数量"><span>{{ fieldForm.minQuantity }}+</span></a-form-model-item>
        <a-form-model-item :label="fieldForm.field === 'stock' ? '库存' : '单价'">
          <a-input v-model="fieldForm.value" @pressEnter="saveField" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-modal
      v-model="visible"
      :title="form.id ? '编辑产品' : '新增产品'"
      :width="1100"
      :confirm-loading="saving"
      :mask-closable="false"
      ok-text="保存"
      cancel-text="取消"
      @ok="handleOk"
    >
      <a-tabs v-model="activeTab">
        <a-tab-pane key="basic" tab="基本信息">
          <a-form-model ref="productForm" :model="form" :rules="rules" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
            <div class="form-grid">
              <a-form-model-item label="产品名称" prop="name"><a-input v-model="form.name" /></a-form-model-item>
              <a-form-model-item label="产品型号" prop="model"><a-input v-model="form.model" /></a-form-model-item>
              <a-form-model-item label="品牌" prop="brand"><a-input v-model="form.brand" /></a-form-model-item>
              <a-form-model-item label="批次" prop="batchNo"><a-input v-model="form.batchNo" maxlength="120" /></a-form-model-item>
              <a-form-model-item label="产品封装" prop="packageName"><a-input v-model="form.packageName" placeholder="例如：SOP-8、0603" /></a-form-model-item>
              <a-form-model-item label="包装数量" prop="packagingQuantity"><a-input v-model="form.packagingQuantity" /></a-form-model-item>
              <a-form-model-item label="重量(g)" prop="weightGrams"><a-input v-model="form.weightGrams" /></a-form-model-item>
              <a-form-model-item label="产品大项" prop="categoryId">
                <a-select v-model="form.categoryId" placeholder="请选择产品大项" @change="handleFormCategoryChange">
                  <a-select-option v-for="item in categoryTree" :key="item.id" :value="item.id">{{ item.name }}</a-select-option>
                </a-select>
              </a-form-model-item>
              <a-form-model-item label="产品种类" prop="subCategoryId">
                <a-select v-model="form.subCategoryId" placeholder="请选择产品种类" :disabled="!form.categoryId" @change="handleFormSubCategoryChange">
                  <a-select-option v-for="item in formSubCategories" :key="item.id" :value="item.id">{{ item.name }}</a-select-option>
                </a-select>
              </a-form-model-item>
              <a-form-model-item label="库存" prop="stock"><a-input v-model="form.stock" /></a-form-model-item>
              <a-form-model-item label="起购数量" prop="minQuantity"><a-input v-model="form.minQuantity" /></a-form-model-item>
              <a-form-model-item label="排序"><a-input-number v-model="form.sortOrder" :min="0" :precision="0" class="number-input" /></a-form-model-item>
              <a-form-model-item label="当前状态"><a-tag :color="form.status === 1 ? 'green' : 'red'">{{ form.status === 1 ? '上架' : '下架' }}</a-tag></a-form-model-item>
            </div>

            <a-form-model-item label="价格梯度" :label-col="{ span: 3 }" :wrapper-col="{ span: 21 }">
              <div class="price-tier-list">
                <div v-for="(tier, index) in form.priceTiers" :key="index" class="price-tier-row">
                  <a-input v-model="tier.minQuantity" placeholder="数量" />
                  <a-input v-model="tier.price" placeholder="单价" />
                  <span>{{ tier.minQuantity || form.minQuantity || 1 }}+ 件</span>
                  <a-button icon="delete" @click="removePriceTier(index)" />
                </div>
                <a-button icon="plus" @click="addPriceTier">添加价格梯度</a-button>
              </div>
            </a-form-model-item>

            <a-form-model-item label="产品图片" :label-col="{ span: 3 }" :wrapper-col="{ span: 21 }">
              <a-upload
                name="file"
                list-type="picture-card"
                multiple
                :file-list="productImageFileList"
                :action="imageUploadAction"
                :headers="uploadHeaders"
                :before-upload="beforeImageUpload"
                @change="handleProductImagesChange"
              >
                <div><a-icon type="plus" /><div class="upload-text">上传图片</div></div>
              </a-upload>
            </a-form-model-item>

            <a-form-model-item label="产品简介" prop="summary" :label-col="{ span: 3 }" :wrapper-col="{ span: 21 }">
              <a-textarea v-model="form.summary" :rows="4" :max-length="500" />
            </a-form-model-item>
          </a-form-model>
        </a-tab-pane>

        <a-tab-pane key="params" tab="产品参数">
          <div class="tab-toolbar">
            <a-button type="primary" icon="plus" @click="addParam">添加参数</a-button>
          </div>
          <a-table :columns="paramColumns" :data-source="form.params" :pagination="false" rowKey="rowKey" bordered>
            <template slot="paramName" slot-scope="text, record"><a-input v-model="record.paramName" placeholder="产品属性" /></template>
            <template slot="paramValue" slot-scope="text, record"><a-input v-model="record.paramValue" placeholder="属性值" /></template>
            <template slot="paramAction" slot-scope="text, record, index"><a class="danger-link" @click="removeParam(index)">删除</a></template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="files" tab="产品文件">
          <div class="file-upload-tip">支持图片、PDF、Word、Excel、TXT、ZIP，可一次混合上传多个文件，单个文件不超过20MB。</div>
          <a-upload-dragger
            name="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
            :file-list="uploadFileList"
            :action="fileUploadAction"
            :headers="uploadHeaders"
            :show-upload-list="false"
            :before-upload="beforeProductFileUpload"
            @change="handleFilesChange"
          >
            <p class="ant-upload-drag-icon"><a-icon type="inbox" /></p>
            <p class="ant-upload-text">点击或拖拽上传产品文件</p>
          </a-upload-dragger>
          <div v-if="uploadingProductFiles.length" class="uploading-file-list">
            <div class="product-file-list-title">正在上传（{{ uploadingProductFiles.length }}）</div>
            <div v-for="file in uploadingProductFiles" :key="file.uid" class="product-file-list-row">
              <a-icon type="loading" />
              <span class="product-file-list-name" :title="file.name">{{ file.name }}</span>
              <span>上传中</span>
            </div>
          </div>
          <div v-if="form.files.length" class="product-file-grid">
            <div class="product-file-list-title">已上传文件（{{ form.files.length }}）</div>
            <div class="uploaded-file-list">
              <div v-for="(file, index) in form.files" :key="'name-' + file.rowKey" class="product-file-list-row">
                <a-icon type="paper-clip" />
                <span class="product-file-list-name" :title="file.fileName">{{ file.fileName }}</span>
                <a-tag>{{ file.fileType }}</a-tag>
                <a @click="previewFile(file)">打开</a>
                <a class="danger-link" @click="removeFile(index)">删除</a>
              </div>
            </div>
            <div v-for="(file, index) in form.files" :key="file.rowKey" class="product-file-card">
              <div class="file-preview" @click="previewFile(file)">
                <img v-if="isImageFile(file)" :src="resolveFilePreviewUrl(file)" :alt="file.fileName">
                <iframe v-else-if="isPdfFile(file)" :src="resolveFilePreviewUrl(file)" :title="file.fileName"></iframe>
                <div v-else class="document-preview">
                  <a-icon type="file" />
                  <strong>{{ file.fileType }}</strong>
                </div>
              </div>
              <a-input v-model="file.fileName" />
              <div class="file-card-actions">
                <a-tag>{{ file.fileType }}</a-tag>
                <a @click="previewFile(file)">打开</a>
                <a class="danger-link" @click="removeFile(index)">删除</a>
              </div>
            </div>
          </div>
          <a-empty v-if="!form.files.length && !uploadingProductFiles.length" description="暂无产品文件" />
        </a-tab-pane>
      </a-tabs>
    </a-modal>
  </div>
</template>

<script>
import {
  API_BASE_URL,
  getProduct,
  getProductList,
  saveProduct,
  deleteProduct,
  updateProductStatus,
  getProductCategoryTree,
  importProducts,
  getProductImportTemplate,
  uploadProductImportImage,
  uploadProductImportFile
} from '@/api'
import adminSession from '@/utils/adminSession'
import request from '@/utils/request'
import { validQuantity, validPrice, validateTiers } from './productValidation'

export default {
  data() {
    return {
      query: { name: '', models: '', brands: '', categoryId: undefined, subCategoryId: undefined },
      fieldVisible: false,
      fieldSaving: false,
      fieldForm: {},
      categoryTree: [],
      data: [],
      loading: false,
      saving: false,
      importVisible: false,
      importing: false,
      templateDownloading: false,
      importFileList: [],
      importImageFileList: [],
      importPdfFileList: [],
      imageCheck: null,
      pdfCheck: null,
      visible: false,
      activeTab: 'basic',
      form: this.createEmptyForm(),
      productImageFileList: [],
      uploadFileList: [],
      imageUploadAction: API_BASE_URL + '/upload/image',
      fileUploadAction: API_BASE_URL + '/upload/file',
      pagination: { current: 1, pageSize: 10, total: 0 },
      columns: [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '产品名称', dataIndex: 'name', width: 180 },
        { title: '型号', dataIndex: 'model', width: 150 },
        { title: '品牌', dataIndex: 'brand', width: 110 },
        { title: '批次', dataIndex: 'batchNo', width: 110 },
        { title: '封装', dataIndex: 'packageName', width: 120 },
        { title: '包装数量', dataIndex: 'packagingQuantity', width: 100 },
        { title: '重量(g)', dataIndex: 'weightGrams', width: 100 },
        { title: '产品大项', dataIndex: 'categoryName', width: 110 },
        { title: '产品种类', dataIndex: 'subCategoryName', width: 130 },
        { title: '价格梯度', dataIndex: 'price', width: 230, scopedSlots: { customRender: 'price' } },
        { title: '库存', dataIndex: 'stock', width: 90, scopedSlots: { customRender: 'stock' } },
        { title: '起购数量', dataIndex: 'minQuantity', width: 100 },
        { title: '状态', dataIndex: 'status', width: 80, scopedSlots: { customRender: 'status' } },
        { title: '操作', key: 'action', width: 190, fixed: 'right', scopedSlots: { customRender: 'action' } }
      ],
      paramColumns: [
        { title: '产品属性', dataIndex: 'paramName', scopedSlots: { customRender: 'paramName' } },
        { title: '属性值', dataIndex: 'paramValue', scopedSlots: { customRender: 'paramValue' } },
        { title: '操作', key: 'action', width: 90, scopedSlots: { customRender: 'paramAction' } }
      ],
      rules: {
        name: [{ required: true, message: '请填写产品名称', trigger: 'blur' }],
        model: [{ required: true, message: '请填写产品型号', trigger: 'blur' }],
        brand: [{ required: true, message: '请填写品牌', trigger: 'blur' }],
        batchNo: [{ required: true, message: '请填写批次', trigger: 'blur' }],
        categoryId: [{ required: true, message: '请选择产品大项', trigger: 'change' }],
        subCategoryId: [{ required: true, message: '请选择产品种类', trigger: 'change' }],
        stock: [{ required: true, message: '请填写库存', trigger: 'change' }],
        minQuantity: [{ required: true, message: '请填写起购数量', trigger: 'change' }],
        summary: [{ required: true, message: '请填写产品简介', trigger: 'blur' }]
      }
    }
  },
  computed: {
    uploadHeaders() {
      const token = adminSession.getAdminToken()
      return token ? { Authorization: 'Bearer ' + token } : {}
    },
    querySubCategories() {
      const category = this.categoryTree.find(item => item.id === this.query.categoryId)
      return category && category.children ? category.children : []
    },
    formSubCategories() {
      const category = this.categoryTree.find(item => item.id === this.form.categoryId)
      return category && category.children ? category.children : []
    },
    uploadingProductFiles() {
      return this.uploadFileList.filter(file => file.status === 'uploading')
    }
  },
  created() {
    this.loadCategoryTree()
    this.loadData()
  },
  methods: {
    createEmptyForm() {
      return {
        name: '', model: '', brand: '', batchNo: '', categoryId: undefined, categoryName: '',
        subCategoryId: undefined, subCategoryName: '', category: '', packageName: '',
        stock: 0, minQuantity: 1, packagingQuantity: null, weightGrams: null, sortOrder: 0, status: 1, summary: '',
        coverImage: '', productImages: '[]', price: null,
        priceTiers: [], params: [], files: []
      }
    },
    loadCategoryTree() {
      getProductCategoryTree().then(res => { this.categoryTree = res.data || [] })
    },
    loadData() {
      this.loading = true
      return getProductList({ ...this.query, page: this.pagination.current, pageSize: this.pagination.pageSize })
        .then(res => {
          this.data = res.data.records || []
          this.pagination.total = res.data.total || 0
        })
        .finally(() => { this.loading = false })
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { name: '', models: '', brands: '', categoryId: undefined, subCategoryId: undefined }
      this.handleSearch()
    },
    openFieldEdit(record, field, tier) {
      const value = field === 'stock' ? record.stock : (tier ? tier.price : record.price)
      this.fieldForm = { id: record.id, model: record.model, field, value: String(value == null ? '' : value),
        expectedValue: value, priceTierId: tier ? tier.id : null, minQuantity: tier ? tier.minQuantity : null }
      this.fieldVisible = true
    },
    saveField() {
      if (this.fieldSaving) return
      const { id, field, value, expectedValue, priceTierId } = this.fieldForm
      if (field === 'stock' ? !validQuantity(value) : !validPrice(value)) {
        return this.$message.warning(field === 'stock' ? '库存必须为0至2147483647之间的整数' : '单价最多3位小数且不超过999999999.999')
      }
      this.fieldSaving = true
      request({ url: `/product/${id}/field`, method: 'post', data: { field, value, expectedValue, priceTierId } })
        .then(() => {
          this.$message.success('保存成功')
          this.fieldVisible = false
          return this.loadData()
        })
        .catch(() => { this.fieldVisible = false; this.loadData() })
        .finally(() => { this.fieldSaving = false })
    },
    handleCreate() {
      this.form = this.createEmptyForm()
      this.productImageFileList = []
      this.uploadFileList = []
      this.activeTab = 'basic'
      this.visible = true
      this.$nextTick(() => this.$refs.productForm && this.$refs.productForm.clearValidate())
    },
    handleEdit(record) {
      getProduct(record.id).then(res => {
        const product = res.data || record
        this.form = {
          ...this.createEmptyForm(),
          ...product,
          packagingQuantity: product.packagingQuantity == null ? null : product.packagingQuantity,
          packageName: this.extractPackageName(product),
          minQuantity: Math.max(1, Number(product.minQuantity || 1)),
          priceTiers: this.normalizePriceTiers(product),
          params: this.normalizeParams(product.params),
          files: this.normalizeFiles(product.files)
        }
        this.productImageFileList = this.buildImageFileList(product.productImages, product.coverImage)
        this.uploadFileList = []
        this.activeTab = 'basic'
        this.visible = true
      })
    },
    handleOk() {
      this.activeTab = 'basic'
      this.$nextTick(() => {
        this.$refs.productForm.validate(valid => {
          if (!valid) return
          if (!validQuantity(this.form.stock)) return this.$message.warning('库存必须为0至2147483647之间的整数')
          if (!validQuantity(this.form.minQuantity, 1)) return this.$message.warning('起购数量必须为正整数且不超过2147483647')
          const packaging = String(this.form.packagingQuantity == null ? '' : this.form.packagingQuantity).trim()
          if (packaging && !validQuantity(packaging, 1)) return this.$message.warning('包装数量填写时必须为正整数且不超过2147483647')
          this.form.packagingQuantity = packaging ? Number(packaging) : null
          const weight = String(this.form.weightGrams == null ? '' : this.form.weightGrams).trim()
          if (!/^\d+(\.\d{1,3})?$/.test(weight) || Number(weight) <= 0 || Number(weight) > 999999999.999) {
            return this.$message.warning('重量必须为大于0的克数，最多3位小数')
          }
          this.form.weightGrams = Number(weight)
          const tierError = validateTiers(this.form.priceTiers, this.form.minQuantity)
          if (tierError) return this.$message.warning(tierError)
          const images = this.productImageFileList
            .filter(file => file.status === 'done')
            .map(file => this.toStorageAssetUrl(file.storageUrl || file.url))
            .filter(Boolean)
          const tiers = this.normalizePriceTiers(this.form)
          this.fillCategoryNames()
          this.form.priceTiers = tiers
          this.form.price = tiers.length ? tiers[0].price : null
          this.form.productImages = JSON.stringify(images)
          this.form.coverImage = images[0]
          this.form.params = this.form.params.filter(item => item.paramName && item.paramValue && !this.isPackageParamName(item.paramName))
          this.form.files = this.form.files.filter(item => item.fileName && item.fileUrl)
          this.saving = true
          saveProduct(this.form).then(() => {
            this.$message.success('产品保存成功')
            this.visible = false
            this.loadData()
          }).finally(() => { this.saving = false })
        })
      })
    },
    handleToggleStatus(record) {
      const status = record.status === 1 ? 0 : 1
      updateProductStatus({ id: record.id, status }).then(() => {
        this.$message.success(status === 1 ? '产品已上架' : '产品已下架')
        this.loadData()
      })
    },
    handleDelete(id) {
      deleteProduct({ id }).then(() => {
        this.$message.success('删除成功')
        this.loadData()
      })
    },
    openImport() {
      this.clearImportImages()
      this.importPdfFileList = []
      this.importFileList = []
      this.imageCheck = null
      this.pdfCheck = null
      this.importVisible = true
    },
    closeImport() {
      if (this.importing) return
      this.importFileList = []
      this.clearImportImages()
      this.importPdfFileList = []
      this.imageCheck = null
      this.pdfCheck = null
    },
    beforeImportUpload(file) {
      const name = String(file.name || '').toLowerCase()
      const validType = name.endsWith('.xlsx')
      const validSize = file.size / 1024 / 1024 <= 10
      if (!validType) this.$message.error('仅支持上传 .xlsx 格式的产品文件')
      if (!validSize) this.$message.error('产品 Excel 文件大小不能超过 10MB')
      if (!validType || !validSize) return false
      this.importFileList = [file]
      this.$nextTick(() => this.checkImportAssets())
      return false
    },
    removeImportFile() {
      if (this.importing) return false
      this.importFileList = []
      this.imageCheck = null
      this.pdfCheck = null
      return true
    },
    beforeImportImageUpload(file) {
      const name = String(file.name || '')
      const validType = (file.type && file.type.indexOf('image/') === 0) || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name)
      const validSize = file.size / 1024 / 1024 <= 5
      const duplicated = this.importImageFileList.some(item => String(item.name || '').toLowerCase() === name.toLowerCase())
      if (!validType) this.$message.error(`${name} 不是支持的图片格式`)
      if (!validSize) this.$message.error(`${name} 大小超过 5MB`)
      if (duplicated) this.$message.warning(`图片 ${name} 已经选择，请勿重复上传`)
      if (!validType || !validSize || duplicated) return false
      const previewUrl = URL.createObjectURL(file)
      this.importImageFileList.push({
        uid: file.uid || `import-image-${Date.now()}-${this.importImageFileList.length}`,
        name,
        size: file.size,
        type: file.type,
        status: 'done',
        originFileObj: file,
        url: previewUrl,
        thumbUrl: previewUrl,
        localPreviewUrl: previewUrl
      })
      this.$nextTick(() => this.checkImportAssets())
      return false
    },
    removeImportImage(file) {
      if (this.importing) return false
      if (file.localPreviewUrl) URL.revokeObjectURL(file.localPreviewUrl)
      this.importImageFileList = this.importImageFileList.filter(item => item.uid !== file.uid)
      this.$nextTick(() => this.checkImportAssets())
      return true
    },
    previewImportImage(file) {
      const url = file.thumbUrl || file.url
      if (url) window.open(url, '_blank')
    },
    clearImportImages() {
      this.importImageFileList.forEach(file => {
        if (file.localPreviewUrl) URL.revokeObjectURL(file.localPreviewUrl)
      })
      this.importImageFileList = []
    },
    beforeImportPdfUpload(file) {
      const name = String(file.name || '')
      const validType = /\.pdf$/i.test(name)
      const validSize = file.size / 1024 / 1024 <= 20
      const duplicated = this.importPdfFileList.some(item => String(item.name || '').toLowerCase() === name.toLowerCase())
      if (!validType) this.$message.error(`${name} 不是 PDF 文件`)
      if (!validSize) this.$message.error(`${name} 大小超过 20MB`)
      if (duplicated) this.$message.warning(`PDF ${name} 已经选择，请勿重复上传`)
      if (!validType || !validSize || duplicated) return false
      this.importPdfFileList.push({
        uid: file.uid || `import-pdf-${Date.now()}-${this.importPdfFileList.length}`,
        name,
        size: file.size,
        type: file.type,
        status: 'done',
        originFileObj: file
      })
      this.$nextTick(() => this.checkImportAssets())
      return false
    },
    removeImportPdf(file) {
      if (this.importing) return false
      this.importPdfFileList = this.importPdfFileList.filter(item => item.uid !== file.uid)
      this.$nextTick(() => this.checkImportAssets())
      return true
    },
    async checkImportAssets(showMessage = false) {
      const uploadFile = this.importFileList[0]
      const file = uploadFile && (uploadFile.originFileObj || uploadFile)
      if (!file) {
        this.imageCheck = null
        return false
      }
      try {
        const excelModule = await import('exceljs')
        const ExcelJS = excelModule.default || excelModule
        const workbook = new ExcelJS.Workbook()
        await workbook.xlsx.load(await file.arrayBuffer())
        const sheet = workbook.worksheets[0]
        if (!sheet) throw new Error('Excel 中没有工作表')
        const cellText = cell => {
          if (!cell) return ''
          if (typeof cell.text === 'string') return cell.text.trim()
          return String(cell.value == null ? '' : cell.value).trim()
        }
        const normalizeHeader = value => String(value || '').replace(/[\s*＊:_：-]/g, '')
        let headerRow = 0
        let imageColumn = 0
        let fileColumn = 0
        const scanRows = Math.min(sheet.rowCount, 10)
        for (let rowNumber = 1; rowNumber <= scanRows && !imageColumn; rowNumber += 1) {
          sheet.getRow(rowNumber).eachCell((cell, columnNumber) => {
            const header = normalizeHeader(cellText(cell))
            if (['产品图片', '商品图片', '图片地址'].indexOf(header) >= 0) {
              headerRow = rowNumber
              imageColumn = columnNumber
            }
            if (['产品文件', '商品文件', 'PDF文件'].indexOf(header) >= 0) fileColumn = columnNumber
          })
        }
        if (!imageColumn) throw new Error('未找到“产品图片”列，请使用最新导入模板')
        const referenced = []
        for (let rowNumber = headerRow + 1; rowNumber <= sheet.rowCount; rowNumber += 1) {
          cellText(sheet.getRow(rowNumber).getCell(imageColumn))
            .split(/[,，;；\r\n]+/)
            .map(name => name.trim())
            .filter(Boolean)
            .forEach(name => {
              if (!referenced.some(item => item.toLowerCase() === name.toLowerCase())) referenced.push(name)
            })
        }
        const selected = this.importImageFileList.map(item => item.name).filter(Boolean)
        const missing = referenced.filter(name => !selected.some(item => item.toLowerCase() === name.toLowerCase()))
        const extra = selected.filter(name => !referenced.some(item => item.toLowerCase() === name.toLowerCase()))
        this.imageCheck = {
          ok: referenced.length > 0 && missing.length === 0 && extra.length === 0,
          missing,
          extra,
          matchedCount: referenced.length - missing.length
        }
        const referencedPdfs = []
        if (fileColumn) {
          for (let rowNumber = headerRow + 1; rowNumber <= sheet.rowCount; rowNumber += 1) {
            cellText(sheet.getRow(rowNumber).getCell(fileColumn))
              .split(/[,，;；\r\n]+/)
              .map(name => name.trim())
              .filter(Boolean)
              .forEach(name => {
                if (!referencedPdfs.some(item => item.toLowerCase() === name.toLowerCase())) referencedPdfs.push(name)
              })
          }
        }
        const selectedPdfs = this.importPdfFileList.map(item => item.name).filter(Boolean)
        const missingPdfs = referencedPdfs.filter(name => !selectedPdfs.some(item => item.toLowerCase() === name.toLowerCase()))
        const extraPdfs = selectedPdfs.filter(name => !referencedPdfs.some(item => item.toLowerCase() === name.toLowerCase()))
        this.pdfCheck = referencedPdfs.length || selectedPdfs.length ? {
          ok: missingPdfs.length === 0 && extraPdfs.length === 0,
          missing: missingPdfs,
          extra: extraPdfs,
          matchedCount: referencedPdfs.length - missingPdfs.length
        } : null
        const assetsMatched = this.imageCheck.ok && (!this.pdfCheck || this.pdfCheck.ok)
        if (showMessage && !assetsMatched) this.$message.warning('请补齐缺少的图片或PDF，并移除表格未引用的多余文件')
        return assetsMatched
      } catch (error) {
        this.imageCheck = { ok: false, missing: [], extra: [], matchedCount: 0, parseError: error.message || '无法读取产品 Excel' }
        this.pdfCheck = null
        if (showMessage) this.$message.error(error.message || '无法读取产品 Excel，请重新下载模板填写')
        return false
      }
    },
    async uploadImportImages() {
      const mappings = {}
      const extras = (this.imageCheck && this.imageCheck.extra) || []
      const files = this.importImageFileList.filter(item => !extras.some(name => name.toLowerCase() === item.name.toLowerCase()))
      for (const item of files) {
        const file = item.originFileObj || item
        const response = await uploadProductImportImage(file)
        const data = response.data || {}
        const path = data.path || data.url
        if (!path) throw new Error(`图片 ${item.name} 上传失败`)
        mappings[item.name] = path
      }
      return mappings
    },
    async uploadImportPdfs() {
      const mappings = {}
      const extras = (this.pdfCheck && this.pdfCheck.extra) || []
      const files = this.importPdfFileList.filter(item => !extras.some(name => name.toLowerCase() === item.name.toLowerCase()))
      for (const item of files) {
        const file = item.originFileObj || item
        const response = await uploadProductImportFile(file)
        const data = response.data || {}
        const path = data.path || data.url
        if (!path) throw new Error(`PDF ${item.name} 上传失败`)
        mappings[item.name] = path
      }
      return mappings
    },
    async submitImport() {
      if (this.importing) return
      const uploadFile = this.importFileList[0]
      const file = uploadFile && (uploadFile.originFileObj || uploadFile)
      if (!file) return this.$message.warning('请先选择需要导入的 Excel 文件')
      const assetsMatched = await this.checkImportAssets(false)
      if (!this.imageCheck) return
      if (this.imageCheck.parseError) return this.$message.error(this.imageCheck.parseError)
      if (!assetsMatched) {
        const content = this.formatAssetMismatch()
        const confirmed = await this.confirmMissingAssets(content)
        if (!confirmed) return
        return this.startProductImport(file, true)
      }
      return this.startProductImport(file, false)
    },
    confirmMissingAssets(content) {
      return new Promise(resolve => {
        this.$confirm({
          title: '部分图片或PDF未匹配',
          content: `${content}。如果确认无需补充，可以继续导入。`,
          okText: '继续导入',
          cancelText: '返回补充',
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        })
      })
    },
    formatAssetMismatch() {
      const parts = []
      if (this.imageCheck && this.imageCheck.missing.length) parts.push(`未上传图片：${this.imageCheck.missing.join('、')}`)
      if (this.pdfCheck && this.pdfCheck.missing.length) parts.push(`未上传PDF：${this.pdfCheck.missing.join('、')}`)
      if (this.imageCheck && this.imageCheck.extra.length) parts.push(`多余图片：${this.imageCheck.extra.join('、')}`)
      if (this.pdfCheck && this.pdfCheck.extra.length) parts.push(`多余PDF：${this.pdfCheck.extra.join('、')}`)
      return parts.join('；')
    },
    async startProductImport(file, allowMissingAssets) {
      this.importing = true
      try {
        const imageMappings = await this.uploadImportImages()
        const fileMappings = await this.uploadImportPdfs()
        await this.runImport(file, false, imageMappings, fileMappings, allowMissingAssets)
        return true
      } catch (error) {
        this.$message.error(error.message || '产品文件上传或导入失败，请重试')
        this.importing = false
        return false
      }
    },
    runImport(file, createMissingCategories, imageMappings, fileMappings, allowMissingAssets) {
      this.importing = true
      return importProducts(file, createMissingCategories, imageMappings, fileMappings, allowMissingAssets).then(res => {
        const result = res.data || {}
        if (result.needsCategoryCreation) {
          this.importing = false
          const description = this.formatMissingCategories(result.missingCategories)
          this.$confirm({
            title: '发现不存在的类目',
            content: `将新增以下类目：${description}。是否创建并继续导入？`,
            okText: '创建并导入',
            cancelText: '暂不导入',
            onOk: () => this.runImport(file, true, imageMappings, fileMappings, allowMissingAssets)
          })
          return
        }
        const categoryText = result.createdCategoryCount ? `，新增类目 ${result.createdCategoryCount} 个` : ''
        const skippedText = result.skippedCount ? `，跳过重复产品 ${result.skippedCount} 条` : ''
        const paramText = result.updatedParamProductCount ? `，补充或更新 ${result.updatedParamProductCount} 个已有产品的参数` : ''
        const priceText = result.updatedPriceProductCount ? `，补充或更新 ${result.updatedPriceProductCount} 个已有产品的价格阶梯` : ''
        const fileText = result.updatedFileProductCount ? `，补充 ${result.updatedFileProductCount} 个已有产品的PDF文件` : ''
        this.$message.success(`成功导入 ${result.importedCount || 0} 条产品${categoryText}${skippedText}${paramText}${priceText}${fileText}`)
        this.importVisible = false
        this.importFileList = []
        this.clearImportImages()
        this.importPdfFileList = []
        this.imageCheck = null
        this.pdfCheck = null
        this.loadCategoryTree()
        this.pagination.current = 1
        this.loadData()
      }).finally(() => { this.importing = false })
    },
    formatMissingCategories(categories) {
      return (categories || []).map(item => {
        const children = (item.subCategoryNames || []).join('、')
        return children ? `${item.categoryName}（${children}）` : item.categoryName
      }).join('；')
    },
    downloadImportTemplate() {
      this.templateDownloading = true
      getProductImportTemplate().then(response => {
        const url = URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = '产品导入模板.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }).finally(() => { this.templateDownloading = false })
    },
    handleQueryCategoryChange() { this.query.subCategoryId = undefined },
    handleFormCategoryChange(value) {
      const category = this.categoryTree.find(item => item.id === value)
      this.$set(this.form, 'categoryName', category ? category.name : '')
      this.$set(this.form, 'category', category ? category.name : '')
      this.$set(this.form, 'subCategoryId', undefined)
      this.$set(this.form, 'subCategoryName', '')
    },
    handleFormSubCategoryChange(value) {
      const subCategory = this.formSubCategories.find(item => item.id === value)
      this.$set(this.form, 'subCategoryName', subCategory ? subCategory.name : '')
      this.$set(this.form, 'category', subCategory ? subCategory.name : this.form.categoryName)
    },
    fillCategoryNames() {
      const category = this.categoryTree.find(item => item.id === this.form.categoryId)
      const subCategory = this.formSubCategories.find(item => item.id === this.form.subCategoryId)
      this.form.categoryName = category ? category.name : ''
      this.form.subCategoryName = subCategory ? subCategory.name : ''
      this.form.category = subCategory ? subCategory.name : this.form.categoryName
    },
    normalizePriceTiers(product) {
      const tiers = Array.isArray(product.priceTiers) ? product.priceTiers : []
      return tiers.map(item => ({
        minQuantity: item.minQuantity,
        price: item.price
      })).filter(item => String(item.minQuantity == null ? '' : item.minQuantity).trim()
        && String(item.price == null ? '' : item.price).trim())
        .sort((a, b) => Number(a.minQuantity) - Number(b.minQuantity))
    },
    addPriceTier() {
      const tiers = this.form.priceTiers
      const last = tiers.length ? tiers[tiers.length - 1] : { minQuantity: this.form.minQuantity || 1, price: '' }
      tiers.push({ minQuantity: Math.max(this.form.minQuantity || 1, Number(last.minQuantity || 1) * 10), price: last.price })
    },
    removePriceTier(index) {
      this.form.priceTiers.splice(index, 1)
    },
    formatPrice(record) {
      const tiers = this.normalizePriceTiers(record)
      return tiers.map(item => `${item.minQuantity}+ ￥${Number(item.price).toFixed(3)}`).join('，')
    },
    normalizeParams(params) {
      return (Array.isArray(params) ? params : [])
        .filter(item => !this.isPackageParamName(item.paramName))
        .map((item, index) => ({ ...item, rowKey: `param-${item.id || index}-${Date.now()}` }))
    },
    isPackageParamName(name) {
      return ['封装', '通用封装', '封装规格'].indexOf(String(name || '').trim()) > -1
    },
    extractPackageName(product) {
      if (product && product.packageName) return product.packageName
      const packageParam = (product && Array.isArray(product.params) ? product.params : [])
        .find(item => this.isPackageParamName(item.paramName))
      return packageParam ? packageParam.paramValue : ''
    },
    addParam() {
      this.form.params.push({ rowKey: `param-new-${Date.now()}-${this.form.params.length}`, paramName: '', paramValue: '', isVisible: 1 })
    },
    removeParam(index) { this.form.params.splice(index, 1) },
    normalizeFiles(files) {
      return (Array.isArray(files) ? files : []).map((item, index) => ({
        ...item,
        // 表单中保留可迁移的相对路径，只在预览时补全 API 域名。
        fileUrl: this.toStorageAssetUrl(item.fileUrl),
        rowKey: `file-${item.id || index}-${Date.now()}`
      }))
    },
    beforeImageUpload(file) {
      const validType = file.type && file.type.indexOf('image/') === 0
      const validSize = file.size / 1024 / 1024 < 5
      if (!validType) this.$message.error('产品图片仅支持图片格式')
      if (!validSize) this.$message.error('图片大小不能超过5MB')
      return validType && validSize
    },
    beforeProductFileUpload(file) {
      const validSize = file.size / 1024 / 1024 < 20
      if (!validSize) this.$message.error('文件大小不能超过20MB')
      return validSize
    },
    handleProductImagesChange(info) {
      this.productImageFileList = this.normalizeUploadList(info.fileList, true)
    },
    handleFilesChange(info) {
      this.uploadFileList = info.fileList.filter(file => file.status !== 'error')
      const current = info.file
      if (current.status !== 'done' || current._addedToProduct) return
      const data = this.extractUploadData(current.response)
      if (!data.path && !data.url) return
      current._addedToProduct = true
      const fileName = data.filename || current.name
      this.form.files.push({
        rowKey: `file-new-${Date.now()}-${this.form.files.length}`,
        fileName,
        fileType: data.fileType || this.detectFileType(fileName),
        fileUrl: this.toStorageAssetUrl(data.path || data.url),
        isVisible: 1
      })
    },
    normalizeUploadList(fileList) {
      return fileList.map(file => {
        if (file.status === 'done') {
          const data = this.extractUploadData(file.response)
          file.storageUrl = this.toStorageAssetUrl(data.path || data.url || file.storageUrl || file.url)
          file.url = this.normalizeAdminAssetUrl(file.storageUrl)
          file.thumbUrl = file.url
        }
        return file
      }).filter(file => file.status !== 'error')
    },
    extractUploadData(response) {
      let body = response || {}
      if (typeof body === 'string') {
        try { body = JSON.parse(body) } catch (e) { body = {} }
      }
      return body.code === 200 && body.data ? body.data : {}
    },
    buildImageFileList(value, fallbackValue) {
      const images = this.parseImages(value)
      const source = images.length ? images : this.parseImages(fallbackValue)
      return source.map((url, index) => {
        const storageUrl = this.toStorageAssetUrl(url)
        const normalizedUrl = this.normalizeAdminAssetUrl(storageUrl)
        return { uid: `product-${index}`, name: `产品图${index + 1}`, status: 'done', storageUrl, url: normalizedUrl, thumbUrl: normalizedUrl }
      })
    },
    parseImages(value) {
      if (!value) return []
      if (Array.isArray(value)) return value.filter(Boolean)
      if (String(value).indexOf('[') === 0) {
        try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed.filter(Boolean) : [] } catch (e) { return [] }
      }
      return [value]
    },
    detectFileType(name) {
      const match = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/)
      return match ? match[1].toUpperCase() : 'FILE'
    },
    toStorageAssetUrl(url) {
      const value = String(url || '').trim()
      if (!value) return ''
      const uploadIndex = value.indexOf('/uploads/')
      if (uploadIndex >= 0) return value.slice(uploadIndex).split(/[?#]/)[0]
      return value
    },
    normalizeAdminAssetUrl(url) {
      const value = String(url || '').trim()
      if (!value) return ''
      if (value === 'file/fff.png' || value === '/static/file/fff.png') return '/fff.png'
      const uploadIndex = value.indexOf('/uploads/')
      if (uploadIndex >= 0) return API_BASE_URL.replace(/\/$/, '') + value.slice(uploadIndex)
      if (value.indexOf('uploads/') === 0) return API_BASE_URL.replace(/\/$/, '') + '/' + value
      if (/^https?:\/\//i.test(value)) return value
      return value.indexOf('/') === 0 ? value : '/' + value
    },
    isImageFile(file) { return /^(IMAGE|JPG|JPEG|PNG|GIF|WEBP|BMP)$/i.test(file.fileType || '') || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(file.fileUrl || '') },
    isPdfFile(file) { return /^PDF$/i.test(file.fileType || '') || /\.pdf$/i.test(file.fileUrl || '') },
    resolveFilePreviewUrl(file) { return this.normalizeAdminAssetUrl(file && file.fileUrl) },
    previewFile(file) {
      const url = this.resolveFilePreviewUrl(file)
      if (url) window.open(url, '_blank')
    },
    removeFile(index) { this.form.files.splice(index, 1) }
  }
}
</script>

<style scoped>
.product-page { background: #fff; padding: 20px; }
.toolbar { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 20px; }
.query-control { width: 190px; }
.import-actions { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.import-tip { margin-top: 16px; }
.import-guide { margin-bottom: 16px; background: #f7f9fc; border-radius: 6px; }
.import-guide-content { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; color: #595959; line-height: 1.6; }
.import-upload-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
.import-upload-panel { min-width: 0; min-height: 300px; padding: 16px; border: 1px solid #e8e8e8; border-radius: 6px; box-sizing: border-box; }
.import-upload-panel h3 { margin: 0 0 14px; font-size: 15px; }
.import-upload-panel /deep/ .ant-upload.ant-upload-drag { height: 210px; }
.import-upload-panel /deep/ .ant-upload-list { max-height: 120px; overflow-y: auto; }
.image-upload-hint { margin: 4px 0 0; color: #888; }
.image-check-result { margin-top: 16px; }
.action-links { white-space: nowrap; }
.danger-link { color: #f5222d; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 30px; }
.number-input { width: 100%; }
.price-tier-list { display: flex; flex-direction: column; gap: 8px; }
.price-tier-row { display: flex; align-items: center; gap: 10px; }
.price-tier-row .ant-input { width: 180px; }
.price-links { display: flex; flex-direction: column; gap: 4px; }
.upload-text { margin-top: 8px; }
.tab-toolbar { display: flex; justify-content: flex-end; align-items: center; margin-bottom: 16px; }
.file-upload-tip { margin-bottom: 12px; color: #666; }
.uploading-file-list { margin-top: 16px; }
.product-file-list-title { grid-column: 1 / -1; margin: 16px 0 8px; font-weight: 600; color: #333; }
.uploaded-file-list { grid-column: 1 / -1; border: 1px solid #e8e8e8; border-radius: 4px; }
.product-file-list-row { display: flex; align-items: center; gap: 10px; min-height: 42px; padding: 8px 12px; border-bottom: 1px solid #f0f0f0; }
.product-file-list-row:last-child { border-bottom: 0; }
.product-file-list-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.product-file-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; margin-top: 18px; }
.product-file-card { min-width: 0; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fafafa; }
.file-preview { height: 190px; margin-bottom: 10px; overflow: hidden; border-radius: 5px; background: #fff; cursor: pointer; }
.file-preview img,
.file-preview iframe { display: block; width: 100%; height: 100%; border: 0; object-fit: contain; }
.file-preview iframe { pointer-events: none; }
.document-preview { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: #1684ff; }
.document-preview .anticon { font-size: 52px; }
.file-card-actions { display: flex; align-items: center; gap: 10px; margin-top: 10px; }
@media (max-width: 900px) {
  .import-upload-grid { grid-template-columns: 1fr; }
  .import-guide-content { grid-template-columns: 1fr; }
}
@media (min-width: 901px) and (max-width: 1180px) {
  .import-upload-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
